import { ItiHttpService } from "@/iti-core-react/services/iti-http-service";
import { ItiString } from "@/iti-core-react/utils/iti-string";
import { ArtifactDto } from "@/shared/dtos/Persistence/artifact-dto";
import { API } from "../api";
import { PersistedArtifactDto } from "../dtos/Gateway/persisted-artifact-dto";
import {
	CatalogueArtifactDto,
	DatabaseTable,
} from "../dtos/Governance/catalogue-artifact-dto";
import { ArtifactAccessLocationDto } from "../dtos/Persistence/artifact-access-location-dto";
import { ArtifactLocationDto } from "../dtos/Persistence/artifact-location-dto";
import { JsonOrientDto } from "../dtos/Persistence/json-orient-dto";
import { NewArtifactConfiguredDto } from "../dtos/Persistence/new-artifact-configured-dto";
import { SortQueryOptions, buildQueryOptions } from "../dtos/artifact-types";
import { Page } from "../dtos/page-types";

export const ArtifactService = {
	async createArtifact(
		artifact: NewArtifactConfiguredDto,
		showLoading?: boolean,
	): Promise<PersistedArtifactDto> {
		return await ItiHttpService.post(
			`${API.persistence.dataset}/${artifact.datasetId}/artifacts`,
			artifact,
			true,
			showLoading,
		);
	},
	async createArtifactEDC(
		artifact: ArtifactDto,
		showLoading?: boolean,
	): Promise<ArtifactDto> {
		return await ItiHttpService.post(
			`${API.persistence.dataset}/${artifact.datasetId}/artifacts`,
			artifact,
			true,
			showLoading,
		);
	},
	async delete(datasetId: string, artifactId: string): Promise<string> {
		return await ItiHttpService.delete(
			`${API.persistence.dataset}/${datasetId}/artifacts/${artifactId}`,
		);
	},
	async getArtifact(
		id: string,
		showLoading: boolean,
	): Promise<CatalogueArtifactDto> {
		const result = await this.getArtifactByType(
			id,
			"STRUCTURED_DISTRIBUTION",
			showLoading,
		);
		return result as CatalogueArtifactDto;
	},

	async getArtifactByType(
		id: string,
		type: string,
		showLoading: boolean,
		opts?: SortQueryOptions,
	): Promise<Page<DatabaseTable> | CatalogueArtifactDto> {
		const base =
			type === "DATABASE"
				? `${API.governance.databases}/${id}/tables`
				: `${API.governance.artifactsv2}/${id}/columns`;

		const endpoint = `${base}${buildQueryOptions(opts)}`;

		return ItiHttpService.get(endpoint, ItiString.Empty, showLoading);
	},

	async getDatabaseTables(
		databaseId: string,
		showLoading: boolean,
		opts?: SortQueryOptions,
	): Promise<Page<DatabaseTable>> {
		const endpoint = `${API.governance.databases}/${databaseId}/tables${buildQueryOptions(opts)}`;
		return ItiHttpService.get(endpoint, ItiString.Empty, showLoading);
	},

	async getTable(
		id: string,
		showLoading: boolean,
	): Promise<CatalogueArtifactDto> {
		return await ItiHttpService.get(
			`${API.governance.tables}/${id}`,
			ItiString.Empty,
			showLoading,
		);
	},

	async getArtifactLocation(
		datasetId: string,
		artifactId: string,
		showLoading?: boolean,
	): Promise<ArtifactLocationDto> {
		return await ItiHttpService.get(
			`${API.persistence.datasetPersist}/${datasetId}/artifacts/${artifactId}/artifactLocation`,
			showLoading,
		);
	},
	async uploadFile(artifact: ArtifactDto, file: File): Promise<any> {
		const bodyFormData = new FormData();
		const infoToAppend: (keyof ArtifactDto)[] = [
			"datasetId",
			"artifactId",
			"fieldDelimiter",
			"decimalDelimiter",
			"resourceName",
			"fileType",
		];

		for (const element of infoToAppend) {
			bodyFormData.append(element.toString(), artifact[element] as any);
		}
		bodyFormData.append("file", file);

		return await ItiHttpService.postMultiPart(
			`${API.persistence.dataset}/${artifact.datasetId}/artifacts-configured/uploadFile`,
			bodyFormData,
		);
	},
	async getUploadUrl(
		datasetId: string,
		artifactId: string,
	): Promise<ArtifactAccessLocationDto> {
		return ItiHttpService.get(
			`${API.persistence.download}/${datasetId}/artifacts/${artifactId}/upload-location`,
			{},
			false,
		);
	},
	async downloadArtifact(datasetId: string, artifactId: string): Promise<any> {
		return await ItiHttpService.get(
			`${API.persistence.download}/${datasetId}/artifacts/${artifactId}/location`,
		);
	},
	// Anyadir términos a un artefacto
	async addTermsToArtifact(
		artifactId: string,
		termsIds: string[],
	): Promise<any> {
		return await ItiHttpService.post(
			`${API.governance.artifacts}/${artifactId}/terms`,
			termsIds,
		);
	},
	// Anyadir términos a un artefacto v2
	async addTermsToArtifactV2(
		artifactId: string,
		termsIds: string[],
	): Promise<any> {
		return await ItiHttpService.post(
			`${API.governance.artifactsv2}/${artifactId}/terms`,
			termsIds,
		);
	},
	// Eliminar términos de un artefacto
	async removeTermsFromArtifact(
		artifactId: string,
		termsId: any,
	): Promise<any> {
		return await ItiHttpService.delete(
			`${API.governance.artifacts}/${artifactId}/terms`,
			termsId,
		);
	},
	// Eliminar términos de un artefacto v2
	async removeTermsFromArtifactV2(
		artifactId: string,
		termsId: any,
	): Promise<any> {
		return await ItiHttpService.delete(
			`${API.governance.artifactsv2}/${artifactId}/terms/${termsId}`,
		);
	},
	// Añadir términos a una base de datos
	async addTermsToDatabase(
		databaseId: string,
		termsIds: string[],
	): Promise<any> {
		return await ItiHttpService.post(
			`${API.governance.databases}/${databaseId}/terms`,
			termsIds,
		);
	},
	// Eliminar término de una base de datos
	async removeTermFromDatabase(
		databaseId: string,
		termId: string,
	): Promise<any> {
		return await ItiHttpService.delete(
			`${API.governance.databases}/${databaseId}/terms/${termId}`,
		);
	},
	// Añadir términos a una tabla
	async addTermsToTable(tableId: string, termsIds: string[]): Promise<any> {
		return await ItiHttpService.post(
			`${API.governance.tables}/${tableId}/terms`,
			termsIds,
		);
	},
	// Eliminar término de una tabla
	async removeTermFromTable(tableId: string, termId: string): Promise<any> {
		return await ItiHttpService.delete(
			`${API.governance.tables}/${tableId}/terms/${termId}`,
		);
	},
	// Anyadir términos a la columna de un artefacto
	async addTermsToArtifactColumn(columnId: string, termsIds: string[]) {
		return await ItiHttpService.post(
			`${API.governance.artifactColumnsv2}/${columnId}/terms`,
			termsIds,
		);
	},
	// Eliminar términos de la columna de un artefacto
	async removeTermsFromArtifactColumn(columnId: string, termsId: string) {
		return await ItiHttpService.delete(
			`${API.governance.artifactColumnsv2}/${columnId}/terms/${termsId}`,
		);
	},
	// Consultar terminos de una columna de un artefacto
	async getTermsFromArtifactColumn(columnId: string) {
		return await ItiHttpService.get(
			`${API.governance.artifactColumnsv2}/${columnId}/terms`,
		);
	},
	async getTermsFromArtifactColumnV2(columnId: string) {
		const res = await ItiHttpService.get(
			`${API.governance.artifactColumnsv2}/${columnId}`,
		);
		return res?.terms ?? [];
	},
	async uploadFileToMinIO(url: string, file: File) {
		const bodyFormData = new FormData();
		bodyFormData.append("file", file);

		return await ItiHttpService.putWithoutAuth(url, file, false);
	},
	async deleteArtifact(datasetId: any, artifactId: string): Promise<boolean> {
		return await ItiHttpService.delete(
			`${API.gateway.consolidate}/${datasetId}/artifacts/${artifactId}`,
		);
	},
	async getArtifactPreview(artifactId: any) {
		return await ItiHttpService.get(
			`${API.persistence.artifact}/${artifactId}/artifactPreview`,
		);
	},
	async isSampleAvailable(artifactId: string): Promise<boolean> {
		const cacheKey = `sample_available_${artifactId}`;
		const cached = sessionStorage.getItem(cacheKey);

		if (cached !== null) {
			return JSON.parse(cached);
		}

		try {
			const result = await ItiHttpService.get(
				`${API.persistence.artifact}/${artifactId}/artifactPreview-available/`,
			);

			sessionStorage.setItem(cacheKey, JSON.stringify(result));
			return result;
		} catch (error) {
			sessionStorage.setItem(cacheKey, JSON.stringify(false));
			return false;
		}
	},
	async getJsonOrient(): Promise<JsonOrientDto[]> {
		return await ItiHttpService.get(`${API.persistence.orient}`);
	},
	async detectCsvSeparator(file: File): Promise<{
		separator: string;
		separator_score: number;
		decimal_separator: string;
		decimal_score: number;
	}> {
		const formData = new FormData();
		formData.append("file", file);

		const baseUrl = import.meta.env.VITE_BASE_URL as string;
		const url = `${baseUrl}/eda-service/utils/detect-csv-separators`;

		const result = await ItiHttpService.postMultiPart(url, formData);
		return result as {
			separator: string;
			separator_score: number;
			decimal_separator: string;
			decimal_score: number;
		};
	},

	async detectJsonOrient(
		file: File,
	): Promise<{ orient: string; confidence: number }> {
		const formData = new FormData();
		formData.append("file", file);

		const baseUrl = import.meta.env.VITE_BASE_URL as string;
		const url = `${baseUrl}/eda-service/utils/detect-json-orient`;

		const result = await ItiHttpService.postMultiPart(url, formData);
		return result as { orient: string; confidence: number };
	},
};
